<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please upload your consolidated statement to complete your account setup.
          This document helps us verify your financial information and expedite
          the account opening process.
        </p>
        <p>
          <strong>For Trust Accounts:</strong> In addition to the consolidated statement, 
          please also upload any relevant trust documents, including the trust agreement, 
          amendments, and any other supporting documentation related to the trust.
        </p>
      </div>
    </help-drawer>
    <v-form name="ConsolidatedStatement" class="form" data-test-id="consolidated-statement-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.CONSOLIDATED_STATEMENT.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="consolidated-statement-header">
              <span class="headerlight">
                Please upload <strong>Consolidated Statement and Trust Documents (if applicable)</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Uploading your statement...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isConsolidatedStatementComplete && formHidden && !isSyncing"
          :submitted="isConsolidatedStatementComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container large">
              <div class="file-upload-container">
                <v-file-input
                  v-model="selectedFile"
                  :rules="fileRules"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  placeholder="Select your consolidated statement"
                  prepend-icon="mdi-paperclip"
                  outlined
                  :error-messages="fileErrors"
                  @change="$v.selectedFile.$touch()"
                  data-test-id="file-input"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip
                      small
                      label
                      color="primary"
                      data-test-id="selected-file-chip"
                    >
                      {{ text }}
                    </v-chip>
                  </template>
                </v-file-input>
                
                <div class="file-info mt-2">
                  <p class="caption">
                    Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG
                  </p>
                  <p class="caption">
                    Maximum file size: 10MB
                  </p>
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Continue without uploading</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="consolidated-statement-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';
import ApiService from '@/services/api.service';
import { NotificationService, NOTIFICATION_TYPES } from '@/services/notification.service';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default {
  name: 'ConsolidatedStatement',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    selectedFile: null,
    uploading: false,
    MENUS: MENUS,
  }),

  validations() {
    return {
      selectedFile: {
        required,
      },
      validationGroup: ['selectedFile'],
    };
  },

  computed: {
    // Is consolidated statement complete
    isConsolidatedStatementComplete() {
      return this.isPageComplete(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
      return page && page.status === 'IN_PROGRESS';
    },
    
    /* Dynamic errors */
    fileErrors() {
      const errors = [];
      if (!this.$v.selectedFile.$dirty) return errors;
      !this.$v.selectedFile.required &&
        errors.push('Please select a file to upload');
      return errors;
    },
    
    fileRules() {
      return [
        v => !v || v.size < MAX_FILE_SIZE || 'File size should be less than 10 MB!'
      ];
    },
    
    helpTitle() {
      return 'Why do you need this?';
    },
    
    interviewId() {
      // Interview ID is stored as sessionToken in auth state
      return this.$store.state.auth.sessionToken;
    }
  },

  methods: {
    /* Upload the consolidated statement */
    async uploadStatement() {
      if (!this.selectedFile) return;

      const formData = new FormData();
      formData.append('statement', this.selectedFile);

      try {
        const url = `/interviews/${this.interviewId}/consolidated-statement`;
        const response = await ApiService.patch(url, formData);
        return response;
      } catch (error) {
        console.error('Upload failed:', error);
        throw error;
      }
    },

    /* Nav to Next Page in flow */
    async next(route = null) {
      let to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      try {
        // Upload the statement using the dedicated endpoint
        // This endpoint handles both file upload and interview update
        await this.uploadStatement();
        
        // The dedicated endpoint already updates the interview status,
        // so we should NOT call completeStep which uses the regular PATCH endpoint
        
        // Set formHidden to true after successful completion
        this.formHidden = true;
        
        // Fetch updated interview progress to reflect the changes
        await this.$store.dispatch('GET_INTERVIEW_PROGRESS');
        
        this.goNext(to);
      } catch (error) {
        // Handle error - reset syncing state by clearing the IN_PROGRESS status
        const page = this.getPage(this.pages[this.pageIndex]);
        if (page) {
          page.status = null; // Clear in-progress status on error
        }
        
        this.$v.$reset();
        console.error('Failed to upload statement:', error);
        
        // Show error notification
        NotificationService.alert({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Upload Failed',
          message: 'Failed to upload consolidated statement. Please try again.',
          okTitle: 'Ok'
        });
      }
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Mark as skipped
      const data = {
        skipped: true,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          this.goNext(this.pages[this.pageIndex + 1]);
        })
        .catch(() => {});
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // Check if page exists AND is complete before hiding form
      const page = this.getPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
      if (page && page.lastCompleted !== null && page.status === 'SYNCED') {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isConsolidatedStatementComplete(newVal) {
      // Only hide form if page actually exists and is complete
      const page = this.getPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
      if (newVal === true && page && page.lastCompleted !== null) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
    // Initialize form data for current interview
    // Check if page exists AND is complete before hiding form
    const page = this.getPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
    this.formHidden = page && page.lastCompleted !== null && page.status === 'SYNCED';
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // Check if page exists AND is complete before hiding form
      const page = this.getPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
      if (page && page.lastCompleted !== null && page.status === 'SYNCED') {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.file-upload-container {
  margin: 0 auto;
  max-width: 500px;
}

.file-info {
  text-align: center;
  
  p {
    margin: 0;
    color: #666;
  }
}

.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>